import Link from 'next/link';
import { Info } from 'phosphor-icons';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { LinkV2 } from '../../atoms/Link/Link.v2';
import { Popover } from '../../atoms/Popover/Popover';
import { ExpandableGroup } from '../../molecules/ExpandableGroup';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../molecules/Table/Table';
import {
  ProductCompatibilityTableMultiPackagePropsDataWithMicrocopy,
  ProductCompatibilityTableProps,
  ProductCompatibilityTableSinglePackagePropsDataWithMicrocopy,
} from './ProductCompatibilityTable.types';

const renderLabeledTooltip = (
  label: string,
  tooltipMsg: string,
  tooltipHrefLabel: string,
  tooltipHref: string
) => (
  <div className="inline-flex items-center">
    <p className="tiny-text text-content-muted">{label}</p>
    <Popover>
      <Popover.Trigger
        aria-label={tooltipMsg}
        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-grey-500 hover:text-content-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-1 focus-visible:ring-offset-canvas dark:text-content-subtle dark:hover:text-content-muted dark:focus-visible:ring-focus"
      >
        <Info size={16} weight="regular" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Content className="w-64 rounded-md border-transparent bg-grey-700 px-4 py-3 text-white shadow-card [&>div:nth-child(1)]:hidden [&>div:nth-child(2)]:hidden">
        <div
          aria-hidden="true"
          className="absolute -top-1 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 bg-grey-700"
        />
        <p className="text-sm leading-7 text-white">{tooltipMsg}</p>
        <LinkV2 asChild size="small">
          <Link
            href={tooltipHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${tooltipHrefLabel} (${label})`}
            className="text-sm leading-7 text-blue-300 no-underline hover:text-blue-200 hover:no-underline"
          >
            {tooltipHrefLabel}
          </Link>
        </LinkV2>
      </Popover.Content>
    </Popover>
  </div>
);

const ProductCompatibilitySinglePackage = ({
  unityVersion,
  renderPipelines,
  fileSize,
  latestReleaseDate,
  microcopy,
  packageType,
  onPackageDetailsClick,
  className = '',
}: ProductCompatibilityTableSinglePackagePropsDataWithMicrocopy & {
  className?: string;
}) => {
  const formatValue = (value: string | string[]) =>
    Array.isArray(value) ? value.join(', ') : value;

  const rows = [
    {
      key: 'renderPipelines',
      label: microcopy.renderPipelines,
      value: renderPipelines,
    },
    { key: 'fileSize', label: microcopy.fileSize, value: fileSize },
    {
      key: 'packageType',
      label: microcopy.packageType,
      value: packageType,
    },
    {
      key: 'latestRelease',
      label: microcopy.latestRelease,
      value: latestReleaseDate,
    },
  ].filter((row) => row.label && row.value) as Array<{
    key: string;
    label: string;
    value: string | string[];
  }>;

  const LABEL_LENGTH_BREAK_THRESHOLD = 10;

  return (
    <div className={c('rounded-lg border border-line-medium p-4', className)}>
      <p className="tiny-text text-muted-foreground mb-4 caption-top break-all text-left font-medium leading-5">
        {microcopy.title}
      </p>
      {unityVersion && unityVersion.length >= 3 && (
        <ExpandableGroup
          microcopy={{
            supportedUnityVersions: microcopy.supportedUnityVersions,
            expandedTitle: microcopy.expandableGroup?.expandedTitle,
            collapsedTitle: microcopy.expandableGroup?.collapsedTitle,
          }}
          unityVersionList={unityVersion || []}
        />
      )}
      <Table
        className={className}
        role="presentation"
        aria-label={microcopy.title}
      >
        <TableHeader className="sr-only">
          <TableRow>
            <TableHead>{microcopy.headerColumn1}</TableHead>
            <TableHead>{microcopy.headerColumn2}</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody variant="alternate-white">
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableHead scope="row" className="!align-top">
                {row.key === 'renderPipelines'
                  ? renderLabeledTooltip(
                      row.label,
                      microcopy.renderPipelinesTooltip,
                      microcopy.renderPipelinesTooltipLinkLabel,
                      microcopy.renderPipelinesTooltipLinkHref
                    )
                  : row.key === 'packageType'
                    ? renderLabeledTooltip(
                        microcopy.packageType,
                        microcopy.packageTypeTooltip,
                        microcopy.packageTypeTooltipLinkLabel,
                        microcopy.packageTypeTooltipLinkHref
                      )
                    : row.label}
              </TableHead>
              <TableCell
                className={c(
                  'w-40',
                  row.label.length > LABEL_LENGTH_BREAK_THRESHOLD
                    ? 'break-normal'
                    : 'break-all'
                )}
              >
                {formatValue(row.value)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {microcopy.packageDetailsLinkLabel && (
        <LinkV2
          href="#"
          className="pl-3 pt-3 text-sm font-medium"
          aria-label={microcopy.packageDetailsLinkLabel}
          onClick={(e) => {
            if (onPackageDetailsClick) {
              e.preventDefault();
              onPackageDetailsClick();
            }
          }}
        >
          {microcopy.packageDetailsLinkLabel}
        </LinkV2>
      )}
    </div>
  );
};

const ProductCompatibilityMultiPackages = ({
  microcopy,
  packageType,
  latestReleaseDate,
  unityVersion,
  onPackageDetailsClick,
  className = '',
}: ProductCompatibilityTableMultiPackagePropsDataWithMicrocopy & {
  className?: string;
}) => {
  return (
    <div className={c('rounded-lg border border-line-medium p-4', className)}>
      <Table className={className} role="presentation">
        <TableCaption>{microcopy?.title}</TableCaption>

        <TableHeader className="sr-only">
          <TableRow>
            <TableHead>{microcopy.headerColumn1}</TableHead>
            <TableHead>{microcopy.headerColumn2}</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody variant="alternate">
          <TableRow>
            <TableHead scope="row">{microcopy.packageType}</TableHead>
            <TableCell>{packageType}</TableCell>
          </TableRow>
          <TableRow>
            <TableHead scope="row">{microcopy.latestReleaseDate}</TableHead>
            <TableCell>{latestReleaseDate}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {microcopy.tableFooter && (
        <p className="tiny-text px-2.5 pt-2 text-content-muted">
          {microcopy.tableFooter}
        </p>
      )}
      {microcopy.packageDetailsDynamicLinkLabel && (
        <LinkV2
          href="#"
          className="pl-3 pt-5 text-sm font-medium"
          aria-label={microcopy.packageDetailsLink}
          onClick={(e) => {
            if (onPackageDetailsClick) {
              e.preventDefault();
              onPackageDetailsClick();
            }
          }}
        >
          {interpolateStringWithData(microcopy.packageDetailsDynamicLinkLabel, {
            unityVersion:
              unityVersion && unityVersion > 0 ? unityVersion.toString() : '',
          })}
        </LinkV2>
      )}
    </div>
  );
};

export const ProductCompatibilityTable = ({
  microcopy,
  ...props
}: ProductCompatibilityTableProps) => {
  if (props.variant === 'single-package') {
    return (
      <ProductCompatibilitySinglePackage {...props} microcopy={microcopy} />
    );
  }

  return <ProductCompatibilityMultiPackages {...props} microcopy={microcopy} />;
};
